<script lang="ts">
  import { onMount } from "svelte";
  import { Button, Input, Label } from "flowbite-svelte";
  import { handleSuccessfulAuth, redirectUrl } from "../services/security/auth-handler.js";
  import { logAuthAttempt, logAuthError } from "../services/logging/log-handler.js";
  import { writable } from "svelte/store";
  import ToastWarning from "../components/common/ToastWarning.svelte";
  import { getADUserByUsername } from "../services/api/data-handler.js";
  import { initializeOnLoginPage } from "../services/cache/cache-manager.js";
  import { logError, logInfo, logWarning } from "../services/logging/log-handler.js";

  const showError = writable(false);

  let username = "";
  let password = "";
  let isInitializing = true;

  onMount(async () => {
    // Clean up all cached state when login page loads
    // This ensures a fresh start for each login session
    // Note: initializeOnLoginPage() will automatically show previous cleanup logs
    try {
      await initializeOnLoginPage();
    } catch (error) {
      logError(error, "LoginFake", undefined, { message: "Cache cleanup warning" });
    }
    isInitializing = false;
  });

  const submitForm = async (event: Event) => {
    event.preventDefault();

    // Log authentication attempt
    logAuthAttempt("azure-ad", {
      userId: username,
      operationName: "submitForm",
    });

    let url: string = "/";
    const unsubscribe = redirectUrl.subscribe((value) => {
      url = value || "/";
    });
    unsubscribe();

    try {
      // Check if user exists in Azure AD
      const adUserResponse = await getADUserByUsername(username);
      const adUser = adUserResponse.ADUser;

      if (!adUser) {
        // Log authentication failure
        logAuthError(new Error("Invalid username or password"), "azure-ad", {
          userId: username,
          operationName: "submitForm",
          errorMessage: "User not found in Azure AD",
        });
        showError.set(true);
        return;
      }

      // Create the token payload with real AD data in the same format as Azure B2C
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenPayload = {
        exp: currentTime + 12 * 60 * 60, // 12 hours
        nbf: currentTime,
        ver: "1.0",
        iss: "https://fake-auth.local/fake-issuer/v2.0/",
        sub: adUser.oid,
        aud: "1987b0bc-96de-489e-94e6-e8c0659b728b", // Same client ID as real B2C
        nonce: "fake-nonce-" + Math.random().toString(36).substring(7),
        iat: currentTime,
        auth_time: currentTime,
        oid: adUser.oid,
        name: adUser.name,
        emails: [adUser.email],
        tfp: "Fake_SignIn",
      };

      // Create proper JWT format: header.payload.signature
      const header = {
        alg: "RS256",
        kid: "fake-key-id",
        typ: "JWT",
      };

      // Encode header and payload as base64url
      const encodedHeader = btoa(JSON.stringify(header)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
      const encodedPayload = btoa(JSON.stringify(tokenPayload)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");

      // Create fake signature
      const signature = "fake-signature-" + Math.random().toString(36).substring(7);
      const encodedSignature = btoa(signature).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");

      // Combine into JWT format: header.payload.signature
      const fakeToken = `${encodedHeader}.${encodedPayload}.${encodedSignature}`;

      const isAuthenticated = await handleSuccessfulAuth(adUser.oid, fakeToken);

      if (!isAuthenticated) {
        // Log authentication failure from handleSuccessfulAuth
        logAuthError(new Error("Authentication failed in handleSuccessfulAuth"), "azure-ad", {
          userId: username,
          oid: adUser.oid,
          operationName: "submitForm",
          errorMessage: "handleSuccessfulAuth returned false",
        });
        showError.set(true);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      logError(error, "LoginFake", undefined, { message: "Error in authentication flow" });
      // Log authentication failure
      logAuthError(error instanceof Error ? error : new Error("Failed to get user from Azure AD"), "azure-ad", {
        userId: username,
        operationName: "submitForm",
        errorMessage: error instanceof Error ? error.message : "Unknown error",
      });
      showError.set(true);
    }
  };

  // showError.subscribe((value) => {
  //   console.log("showError value:", value);
  // });

  const resetError = () => {
    showError.set(false);
  };
</script>

<div class="login-page">
  <div class="login-container">
    <div id="login" style="width:24rem">
      <div>
        <div class="flex justify-center items-center mb-8">
          <svg role="graphics-document" xmlns="http://www.w3.org/2000/svg" width="239" height="48" viewBox="0 0 239 48" fill="none" preserveAspectRatio="xMinYMid meet">
            <path d="M8.69434 24C8.69434 15.5774 15.5774 8.69434 24 8.69434C32.4226 8.69434 39.3057 15.5774 39.3057 24C39.3057 32.4226 32.4226 39.3057 24 39.3057C15.5774 39.3057 8.69434 32.4226 8.69434 24ZM24 0C10.7774 0 0 10.7774 0 24C0 37.2226 10.7774 48 24 48C37.2226 48 48 37.2226 48 24C48 10.7774 37.2226 0 24 0Z" fill="url(#paint0_linear)"></path>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M186.294 15.2151L193.992 32.1509H191.638L189.736 27.9849H182.853L180.951 32.1509H178.596L186.294 15.2151ZM186.204 20.2868L183.577 26.083H188.83L186.204 20.2868ZM61.0415 18.0227C59.5019 19.6529 58.6868 21.5548 58.6868 24C58.6868 26.4453 59.4113 28.4378 60.951 29.9774C62.4906 31.517 64.483 32.3321 66.8377 32.3321C69.1925 32.3321 71.1849 31.517 72.7245 29.9774C74.2642 28.3472 75.0793 26.3548 75.0793 24C75.0793 21.5548 74.2642 19.5623 72.7245 18.0227C71.1849 16.4831 69.1925 15.668 66.7472 15.668C64.483 15.668 62.6717 16.4831 61.0415 18.0227ZM63.5774 20.3774C64.3925 19.3812 65.4792 18.9283 66.8377 18.9283C68.2868 18.9283 69.3736 19.4717 70.0981 20.3774C70.9132 21.2831 71.366 22.4604 71.366 24C71.366 25.5397 70.9132 26.8076 70.0981 27.7133C69.283 28.7095 68.1962 29.1623 66.8377 29.1623C65.4792 29.1623 64.3925 28.6189 63.5774 27.7133C62.7623 26.717 62.3094 25.5397 62.3094 24.0906C62.3094 22.551 62.7623 21.3736 63.5774 20.3774ZM87.2151 15.8491H81.6906V32.151H85.1321V25.8114L87.1245 25.7208C87.7585 25.7208 88.5736 25.5397 89.4793 25.0869C90.4755 24.634 91.1094 24.0001 91.5623 23.185C92.0151 22.3699 92.1962 21.4642 92.1962 20.5586C92.1962 19.1095 91.7434 18.0227 90.8377 17.117C89.9321 16.302 88.7547 15.8491 87.2151 15.8491ZM85.1321 22.9133V18.7472L85.9472 18.6567C86.7623 18.6567 87.4868 18.8378 87.9396 19.2001C88.3925 19.5623 88.6642 20.1057 88.6642 20.7397C88.6642 21.3736 88.483 21.8265 88.0302 22.2793C87.5774 22.7321 87.034 22.9133 86.4906 22.9133H85.1321ZM98.3547 15.8491H107.502V19.1095H101.887V22.3699H107.502V25.5397H101.887V28.8906H107.502V32.151H98.3547V15.8491ZM128.785 15.8491H125.343H125.253V26.3548L117.826 15.8491H114.385V32.151H117.917V21.6453L125.343 32.151H128.785V15.8491ZM144.815 15.8491H147.079V22.0982H154.868V15.8491H157.042V32.151H154.868V24.3623H147.079V32.151H144.815V15.8491ZM173.434 15.8491H164.377V32.151H173.434V29.9774H166.642V24.3623H173.434V22.1887H166.642V18.1133H173.434V15.8491ZM201.962 15.8491H199.789V32.151H207.306V29.9774H201.962V15.8491ZM210.566 15.8491H220.528V18.1133H216.634V32.151H214.46V18.1133H210.566V15.8491ZM228.679 15.8491H226.415V32.151H228.679V24.3623H236.468V32.151H238.642V15.8491H236.468V22.0982H228.679V15.8491Z"
              fill="#ffffff"
            ></path>
            <defs>
              <linearGradient id="paint0_linear" x1="0.00975798" y1="47.9948" x2="48" y2="47.9948" gradientUnits="userSpaceOnUse">
                <stop stop-color="#808080"></stop>
                <stop offset="0.5" stop-color="#A0A0A0"></stop>
                <stop offset="0.75" stop-color="#D0D0D0"></stop>
                <stop offset="1" stop-color="#FFFFFF"></stop>
              </linearGradient>
            </defs>
          </svg>
          <h1 class="login-logo-title" style="font-size: 2rem; color: white;">
            <span class="app-logo-p1 ml-4">i</span><span class="app-logo-p2">SLR</span>
          </h1>
        </div>
        <div class="p-6 space-y-4 md:space-y-6 sm:p-8">
          <form class="flex flex-col space-y-6" on:submit={submitForm}>
            <Input bind:value={username} type="text" name="username" placeholder="username" required on:input={resetError} />
            <Input bind:value={password} type="password" name="password" placeholder="password" required on:input={resetError} />
            <div class="flex items-start">
              <!-- <Checkbox class="text-white-500 dark:text-white-500">Remember me</Checkbox>
                            <a href="/" class="ml-auto text-sm text-white-500 hover:underline dark:text-white-500">Forgot password?</a> -->
            </div>
            <Button type="submit" class="w-full">Sign in</Button>
          </form>
          {#if $showError}
            <div class="toast-warning-container">
              <ToastWarning on:resetError={resetError} />
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  :global(body) {
    overflow: hidden;
  }

  .login-page {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
  }

  .login-container {
    text-align: center;
    padding: 2rem;
  }
</style>
